import type {
  PullRequestDetail,
  PullRequestListEntry,
  PullRequestListResult,
  ProjectId,
  PullRequestRef,
  PullRequestState,
  SourceControlProviderKind,
} from "@cmmd-forge/contracts";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import { isPullRequestDetailQueryKey, isPullRequestListQueryKey } from "~/lib/pullRequestQueryKeys";

/**
 * The live change-request facts a badge or mini list needs, wherever the query cache has them.
 *
 * This is forge's substitute for t3code's persisted `ThreadPullRequestLink.snapshot`: upstream
 * stores a state snapshot on the link itself, while forge's pull-request data lives in the
 * Command query cache the rest of the suite already reads. The shape travels with the reader
 * instead of being written down, and a cache with nothing in it means "status pending", exactly
 * like an unsynced upstream link.
 */
export interface PullRequestLiveStatus {
  readonly number: number;
  readonly url: string;
  readonly title: string | null;
  readonly state: PullRequestState;
  readonly isDraft: boolean;
  /** Null on a detail read, which carries no host of its own. */
  readonly host: string | null;
  readonly repository: string;
  readonly provider: SourceControlProviderKind;
}

/** What a badge's colour and glyph key off: the state, with a draft folded over open. */
export type PullRequestStatusRole = PullRequestState | "draft";

export function pullRequestStatusRole(input: {
  readonly state: PullRequestState;
  readonly isDraft: boolean;
}): PullRequestStatusRole {
  if (input.state === "open" && input.isDraft) return "draft";
  return input.state;
}

function statusFromDetail(detail: PullRequestDetail): PullRequestLiveStatus {
  return {
    number: detail.number,
    url: detail.url,
    title: detail.title,
    state: detail.state,
    isDraft: detail.isDraft,
    host: null,
    repository: detail.repository,
    provider: detail.provider,
  };
}

function statusFromListEntry(entry: PullRequestListEntry): PullRequestLiveStatus {
  return {
    number: entry.number,
    url: entry.url,
    title: entry.title,
    state: entry.state,
    isDraft: entry.isDraft,
    host: entry.host ?? null,
    repository: entry.repository,
    provider: entry.provider,
  };
}

interface PullRequestStatusCandidate {
  readonly projectId: ProjectId;
  readonly repository: string;
  readonly number: number;
  readonly projectRepositoryId?: number | undefined;
}

function sameReference(candidate: PullRequestStatusCandidate, reference: PullRequestRef): boolean {
  if (candidate.number !== reference.number) return false;
  if (candidate.projectId !== reference.projectId) return false;
  if (candidate.repository.toLowerCase() !== reference.repository.toLowerCase()) return false;
  return !(
    candidate.projectRepositoryId !== undefined &&
    reference.projectRepositoryId !== undefined &&
    candidate.projectRepositoryId !== reference.projectRepositoryId
  );
}

function statusFromDetailQuery(
  query: { readonly queryKey: readonly unknown[]; readonly state: { readonly data: unknown } },
  reference: PullRequestRef,
): PullRequestLiveStatus | null {
  if (!isPullRequestDetailQueryKey(query.queryKey)) return null;
  const detail = query.state.data as PullRequestDetail | undefined;
  if (detail === undefined || !sameReference(detail, reference)) return null;
  return statusFromDetail(detail);
}

function statusFromListQuery(
  query: { readonly queryKey: readonly unknown[]; readonly state: { readonly data: unknown } },
  reference: PullRequestRef,
): PullRequestLiveStatus | null {
  if (!isPullRequestListQueryKey(query.queryKey)) return null;
  const data = query.state.data as InfiniteData<PullRequestListResult> | undefined;
  // A list read is infinite-paginated, but the cache scan trusts nothing it did not write:
  // data shaped any other way answers null rather than throwing.
  if (!data || !Array.isArray(data.pages)) return null;
  for (const entry of data.pages.flatMap((page) => page?.entries ?? [])) {
    if (sameReference(entry, reference)) return statusFromListEntry(entry);
  }
  return null;
}

/**
 * The newest status the cache holds for this change request. Nothing here subscribes: a badge
 * that read reactively would re-render every sidebar row on every listing tick, and "status
 * pending" is the honest answer for a cache that has not been asked yet. Detail answers win over
 * list rows at equal recency because a detail read is the fuller record. A null or undefined
 * client (a render outside the app's QueryClientProvider) answers null for the same reason.
 */
export function readPullRequestLiveStatus(
  queryClient: QueryClient | null | undefined,
  reference: PullRequestRef,
): PullRequestLiveStatus | null {
  if (queryClient === null || queryClient === undefined) return null;
  let best: { readonly status: PullRequestLiveStatus; readonly at: number } | null = null;
  for (const query of queryClient.getQueryCache().getAll()) {
    const at = query.state.dataUpdatedAt;
    const candidate =
      statusFromDetailQuery(query, reference) ?? statusFromListQuery(query, reference);
    if (candidate === null || (best !== null && at <= best.at)) continue;
    best = { at, status: candidate };
  }
  return best?.status ?? null;
}

let pullRequestCacheUpdates = 0;

/**
 * Subscribes to pull-request reads landing in this cache, so a reader that rendered before the
 * answer arrived picks it up when it lands instead of staying at "status pending". The module
 * counter is the snapshot `useSyncExternalStore` compares: cheap, consistent between events,
 * and moved only by a pull-request list or detail write.
 */
export function subscribePullRequestCacheUpdates(
  queryClient: QueryClient | null | undefined,
  onChange: () => void,
): () => void {
  if (queryClient === null || queryClient === undefined) return () => undefined;
  const cache = queryClient.getQueryCache();
  return cache.subscribe((event) => {
    const key = (event as { query?: { queryKey?: readonly unknown[] } }).query?.queryKey;
    if (
      event.type === "updated" &&
      Array.isArray(key) &&
      (isPullRequestDetailQueryKey(key) || isPullRequestListQueryKey(key))
    ) {
      pullRequestCacheUpdates += 1;
      onChange();
    }
  });
}

/** The snapshot a cache-event subscription compares, for `useSyncExternalStore`. */
export function readPullRequestCacheUpdates(): number {
  return pullRequestCacheUpdates;
}
